import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTenant } from '@/contexts/TenantContext';
import { NavOverlay } from '@/components/NavOverlay';
import { SplitGallery } from '@/components/SplitGallery';
import { VerticalGallery } from '@/components/VerticalGallery';
import { HorizontalGallery } from '@/components/HorizontalGallery';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { CriaaBadge } from '@/components/CriaaBadge';
import SiteFooterLinks from '@/components/site/SiteFooterLinks';
import { supabase } from '@/integrations/supabase/client';
import { useTrackPageView } from '@/hooks/useTrackPageView';
import NotFound from './NotFound';
import type { ImagePair } from '@/data/galleryData';
import type { Database } from '@/integrations/supabase/types';

interface FooterLink {
  label: string;
  url: string;
}


type LayoutType = 'A' | 'B' | 'C' | 'D';

function normalizeLayout(layout: string | null | undefined): LayoutType {
  if (layout === 'B' || layout === 'C' || layout === 'D') return layout;
  if (layout === 'vertical-scroll') return 'B';
  if (layout === 'horizontal-scroll') return 'C';
  if (layout === 'split-horizontal') return 'D';
  return 'A';
}

const UserHomePage = () => {
  const { username: paramUsername } = useParams<{ username: string }>();
  const { username: tenantUsername } = useTenant();
  const username = tenantUsername || paramUsername;
  const [userId, setUserId] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [homeLayout, setHomeLayout] = useState<LayoutType>('A');
  const [pairs, setPairs] = useState<ImagePair[]>([]);
  const [showBadge, setShowBadge] = useState(true);
  const [userPlan, setUserPlan] = useState<string>('free');
  const [siteName, setSiteName] = useState<string>('');
  const [footerLinks, setFooterLinks] = useState<FooterLink[]>([]);
  const [textColor, setTextColor] = useState<string>('#000000');
  const [fontFamily, setFontFamily] = useState<string>('IBM Plex Mono');


  // Resolve username -> user_id
  useEffect(() => {
    if (!username) return;

    const resolve = async () => {
      const { data } = await supabase
        .rpc('resolve_username', { p_username: username });

      if (!data || data.length === 0) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setUserId(data[0].user_id);
      setUserPlan(data[0].plan || 'free');
    };

    resolve();
  }, [username]);

  // Track page view
  useTrackPageView(userId, `/${username}`);
  useEffect(() => {
    if (!userId) return;

    const loadData = async () => {
      const [settingsRes, imagesRes] = await Promise.all([
        supabase
          .from('site_settings')
          .select('home_layout_type, show_badge, site_name, footer_links, text_color, font_family')
          .eq('user_id', userId)
          .limit(1)
          .single(),
        supabase
          .from('home_images')
          .select('*')
          .eq('user_id', userId)
          .order('sort_order'),
      ]);

      const settings = settingsRes.data as Database['public']['Tables']['site_settings']['Row'] | undefined;
      setSiteName(settings?.site_name || '');
      setHomeLayout(normalizeLayout(settings?.home_layout_type));
      const badgeSetting = settings?.show_badge ?? true;
      setShowBadge(userPlan === 'free' ? true : badgeSetting);
      setTextColor(settings?.text_color || '#000000');
      setFontFamily(settings?.font_family || 'IBM Plex Mono');
      const rawLinks = settings?.footer_links;
      setFooterLinks(Array.isArray(rawLinks) ? (rawLinks as unknown as FooterLink[]) : []);


      const imgs = (imagesRes.data || []).map((img: any) => ({
        left: img.left_url,
        right: img.right_url,
        title: img.title || undefined,
        video_url: img.video_url || undefined,
        video_display_mode: img.video_display_mode || undefined,
      }));

      setPairs(imgs);
      setLoading(false);
    };

    loadData();
  }, [userId, userPlan]);

  if (notFound) return <NotFound />;

  // No published content — show "em construção"
  if (!loading && pairs.length === 0) {
    return (
      <>
        <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#F7F6F2' }}>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 28, fontWeight: 300, color: '#000', letterSpacing: '0.04em', marginBottom: 16 }}>
            {siteName || username?.toUpperCase() || ''}
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 300, color: 'rgba(0,0,0,0.45)', letterSpacing: '0.06em' }}>
            em construção — retornar em breve
          </div>
        </div>
        <CriaaBadge userId={userId} showBadge={showBadge} />
      </>
    );
  }

  const renderGallery = () => {
    switch (homeLayout) {
      case 'B':
        return <VerticalGallery pairs={pairs} />;
      case 'C':
        return <HorizontalGallery pairs={pairs} />;
      case 'D':
        return <SplitGallery pairs={pairs} layout="horizontal" />;
      case 'A':
      default:
        return <SplitGallery pairs={pairs} />;
    }
  };

  return (
    <>
      <LoadingSpinner visible={loading} />
      <NavOverlay />
      {!loading && renderGallery()}
      {!loading && (
        <SiteFooterLinks
          links={footerLinks}
          textColor={textColor}
          fontFamily={fontFamily}
        />
      )}
      {!loading && <CriaaBadge userId={userId} showBadge={showBadge} />}
    </>
  );

};

export default UserHomePage;
